import React from 'react';
import {
  TASK_TYPES,
  TASK_TYPE_LABELS,
  ASSIGNMENT_PRIORITY_LABELS,
  CALLABLE_TASK_TYPES,
} from '@claimup/shared/constants/assignments';
import axios from 'axios';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import EmailAttachment from '../../ManagementListPage/components/EmailChat/components/EmailAttachment';
import { COMPANY_PROFILE_FIELDS, COMPANY_PROFILE_FIELD_LABELS } from '@claimup/shared/constants/company';
import { baseAPI } from '../../../config';
import { expedientStatusLabel } from '@claimup/shared/constants/expedient';

/**
 * Los peldaños de la escalera del seguimiento de WhatsApp (sección 4 de su
 * prompt). El número lo calcula el backend —días distintos que le hemos escrito
 * sin respuesta— y se enseña aquí para que no haya que preguntarlo.
 */
const PELDANOS = 6;

/**
 * El caso, contado para una persona.
 *
 * Dos reglas que vienen de cómo se usa esto:
 *
 * 1. Primero LA REALIDAD, después la propuesta. Quien revisa tiene que poder
 *    juzgar si el agente ha entendido bien la situación, y para eso necesita
 *    ver la conversación de verdad — no el resumen que hizo el propio agente.
 *    Revisar a la IA usando solo el relato de la IA no es revisar.
 *
 * 2. Nada de nombres de función ni JSON. `update_expedient_status` no le dice
 *    nada a un gestor: "Cambiar el estado: On going → Plan de pagos" sí. El
 *    detalle técnico sigue estando, plegado, para cuando algo falle.
 */

/**
 * Los tipos de tarea que el despachador sella con línea de salida.
 *
 * Se deriva igual que `TIPOS_WHATSAPP` en `services/whatsapp/dispatcher.js`,
 * a propósito: si mañana nace un `follow_up_whatsapp_2`, las dos puntas lo
 * reconocen sin que nadie se acuerde de tocar esta pantalla.
 */
const ES_DE_WHATSAPP = TASK_TYPES.filter(t => t.includes('whatsapp'));

const CANALES = {
  send_whatsapp: 'WhatsApp',
  send_whatsapp_template: 'WhatsApp (plantilla)',
  send_email: 'email',
  send_sms: 'SMS',
};

const fecha = d =>
  d
    ? new Date(d).toLocaleString('es-ES', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

const euros = (n, div = 'EUR') =>
  typeof n === 'number'
    ? n.toLocaleString('es-ES', { style: 'currency', currency: div || 'EUR', maximumFractionDigits: 0 })
    : '—';

const nombreDe = d =>
  [d?.debtorname, d?.debtorlastname].filter(Boolean).join(' ').trim() || 'Sin nombre';

const nombreCliente = c => [c?.username, c?.userlastname].filter(Boolean).join(' ').trim();

/**
 * Lo que pasó pero no se puede leer: una llamada, un email del que solo consta
 * que salió.
 *
 * 🔴 Antes esto era una burbuja de chat con un guion dentro, y atribuida al
 * deudor cuando el evento venía marcado como entrante. En un expediente con
 * correos —y los `email_sent` NUNCA guardan cuerpo— «la conversación» eran
 * cinco guiones seguidos. Un guion no se puede juzgar, y esta pantalla existe
 * precisamente para poder juzgar.
 *
 * Va como apunte centrado, no como mensaje: que se vea que pasó algo y que no
 * se confunda con algo que alguien dijo.
 */
const Apunte = ({ i }) => (
  <div className='flex justify-center mb-2'>
    <p className='text-[11px] text-slate-400 px-3 py-1 bg-slate-100 rounded-lg'>
      {i.evento}
      {i.asunto ? ` · «${i.asunto}»` : ''} · {fecha(i.date)}
    </p>
  </div>
);

/**
 * Por dónde llegó o salió un mensaje. Desde que el deudor puede contestar por
 * SMS, un SMS y un WhatsApp se pintaban igual, y la respuesta tiene que ir por
 * el mismo canal.
 */
const canalDe = tipo => {
  const t = String(tipo || '');
  if (t.startsWith('sms')) return 'SMS';
  if (t.startsWith('whatsapp')) return 'WhatsApp';
  if (t.startsWith('email')) return 'Email';
  return null;
};

/**
 * Dónde empieza, en un correo, lo que se cita de los anteriores: «El lun., …
 * escribió:», «On … wrote:», «-----Original Message-----», «De: … Enviado:» o
 * las líneas con «>». Lo de antes es el correo; lo de después, la cadena.
 */
const INICIO_CITA = /^(>|El .{3,120} escribió:|On .{3,120} wrote:|-{2,}\s*(Original Message|Mensaje original)\s*-{2,}|De: .+\n(Enviado|Fecha|Sent):)/im;

export const partirCorreo = texto => {
  const t = String(texto || '');
  const m = t.match(INICIO_CITA);
  if (!m || m.index === 0) return { propio: t, citado: '' };
  return { propio: t.slice(0, m.index).trimEnd(), citado: t.slice(m.index) };
};

/** A partir de aquí, un correo se pliega. */
const LARGO_CORREO = 400;

/**
 * El cuerpo de un correo, plegable (23/10/2026, pedido por Edgar): los correos
 * largos, y sobre todo las cadenas que repiten todo lo anterior, hacían que no se
 * viera cada mensaje. Se enseña lo que dice ese correo; el resto, a un clic.
 */
const CuerpoDeCorreo = ({ texto }) => {
  const { propio, citado } = partirCorreo(texto);
  const largo = propio.length > LARGO_CORREO;
  const [abierto, setAbierto] = React.useState(false);
  const [verCita, setVerCita] = React.useState(false);
  const visible = largo && !abierto ? `${propio.slice(0, LARGO_CORREO).trimEnd()}…` : propio;
  return (
    <>
      <p className='text-sm text-slate-800 whitespace-pre-wrap'>{visible || '—'}</p>
      {largo && (
        <button type='button' onClick={() => setAbierto(a => !a)} className='text-xs text-indigo-600 hover:underline mt-1'>
          {abierto ? 'Plegar el correo' : 'Ver el correo entero'}
        </button>
      )}
      {citado && (
        <div className='mt-1'>
          <button type='button' onClick={() => setVerCita(v => !v)} className='text-xs text-slate-500 hover:underline'>
            {verCita ? 'Ocultar los correos anteriores' : 'Ver los correos anteriores de la cadena'}
          </button>
          {verCita && (
            <p className='mt-1 border-l-2 border-slate-200 pl-2 text-xs text-slate-500 whitespace-pre-wrap'>{citado}</p>
          )}
        </div>
      )}
    </>
  );
};

/** Un mensaje de la conversación, como en un chat. */
const Mensaje = ({ i }) => {
  const canal = canalDe(i.type);
  const entrante = i.direction === 'incoming';
  const esMedia = i.media_type && i.media_type !== 'text';
  // 🔴 Llegó DESPUÉS de escribirse la propuesta que estás juzgando. Se marca
  // solo si además lo mandó él: que hayamos escrito nosotros después no cambia
  // el juicio sobre este mensaje; que haya escrito él, sí.
  const esNuevo = i.posterior_a_la_propuesta && entrante;

  if (i.evento && !esMedia) return <Apunte i={i} />;

  return (
    <div className={`flex ${entrante ? 'justify-start' : 'justify-end'} mb-2`}>
      <div
        className={`max-w-[80%] rounded-lg px-3 py-2 ${
          esNuevo
            ? 'bg-amber-50 border-2 border-amber-300'
            : entrante
              ? 'bg-white border border-slate-200'
              : 'bg-indigo-50 border border-indigo-100'
        }`}
      >
        {esNuevo && (
          <p className='text-[11px] font-semibold text-amber-800 mb-1 uppercase tracking-wide'>
            Después de escribirse la propuesta
          </p>
        )}
        {esMedia ? (
          <p className='text-sm text-slate-700 italic'>
            📎 {i.filename || i.media_type}
          </p>
        ) : canal === 'Email' ? (
          <CuerpoDeCorreo texto={i.message} />
        ) : (
          <p className='text-sm text-slate-800 whitespace-pre-wrap'>{i.message || '—'}</p>
        )}
        {/* 🔴 Los adjuntos, que no se veían aquí.
            Están guardados desde siempre —3.383 interacciones los traen, con su
            clave de S3— y los enseñaba solo el chat de email. Quien revisaba
            leía «te mando el justificante» sin poder abrir el justificante, y
            tenía que decidir a ciegas o irse a otra pantalla.
            Se reutiliza `EmailAttachment` en vez de escribir otro: ya resuelve
            la URL firmada y su caducidad. */}
        {(i.attachments || []).length > 0 && (
          <div className='mt-2 space-y-1.5'>
            {i.attachments.map((a, n) => (
              <EmailAttachment key={`${a.s3_key}-${n}`} attachment={a} />
            ))}
          </div>
        )}
        <p className='text-[11px] text-slate-400 mt-1'>
          {entrante ? 'El deudor' : 'Nosotros'}
          {canal ? ` · ${canal}` : ''} · {fecha(i.date)}
        </p>
      </div>
    </div>
  );
};

/**
 * La tarea que se va a crear, **como quedará**.
 *
 * No se le pide al modelo que la describa en prosa: se calcula de sus
 * parámetros, que es lo que el ejecutor va a usar. Un modelo a veces lo cuenta
 * bien y a veces no; esto sale siempre igual que la realidad.
 *
 * Los cuatro campos son los que `executeCreateFollowUpAssignment` escribe de
 * verdad. Si alguna vez deja de coincidir, esta vista miente — y una vista
 * previa que miente es peor que no tenerla.
 *
 * ⚠️ `assigned_to` sin valor NO significa «para una persona». Significa que la
 * tarea cae al dueño por defecto, que es **el propio agente** — así que se
 * escribe eso, no un genérico. Y hay un caso en el que eso no basta: si es una
 * tarea de LLAMAR, el agente no puede marcar y la tarea no va a ocurrir. Eso se
 * avisa en ámbar aquí, porque quien revisa es el único que puede corregirlo
 * antes de que la tarea nazca.
 */
const Campo = ({ etiqueta, children, aviso }) => (
  <div className='flex gap-2 text-sm'>
    <span className='w-24 shrink-0 text-slate-400'>{etiqueta}</span>
    <span className={aviso ? 'text-amber-700 font-medium' : 'text-slate-800'}>{children}</span>
  </div>
);

const FichaDeLaTarea = ({ params: p }) => {
  const dias = Number(p.days_from_now);
  let vence = null;
  if (Number.isFinite(dias)) {
    const d = new Date();
    d.setDate(d.getDate() + dias);
    vence = d;
  }

  const alAgente = !p.assigned_to || p.assigned_to === 'AIAgent';
  const esLlamada = CALLABLE_TASK_TYPES.includes(p.task_type);
  // Una llamada en manos del agente no se va a marcar nunca.
  const aviso = alAgente && esLlamada;
  const dueno = alAgente
    ? aviso
      ? 'El agente — y el agente no llama: asígnala a una persona'
      : `El agente${p.assigned_to ? '' : ' (por defecto)'}`
    : p.assigned_to;

  return (
    <div className='rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 space-y-1.5'>
      <Campo etiqueta='Tipo'>{TASK_TYPE_LABELS[p.task_type] || p.task_type || '—'}</Campo>
      <Campo etiqueta='Vence'>
        {vence ? (
          <>
            {vence.toLocaleDateString('es-ES', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
            <span className='text-slate-400'>
              {' '}
              · dentro de {dias} {dias === 1 ? 'día' : 'días'}
            </span>
          </>
        ) : (
          '—'
        )}
      </Campo>
      <Campo etiqueta='Prioridad'>
        {ASSIGNMENT_PRIORITY_LABELS[p.priority] || p.priority || '—'}
      </Campo>
      <Campo etiqueta='Asignada a' aviso={aviso}>
        {dueno}
      </Campo>
      <Campo etiqueta='Descripción'>«{p.description || '—'}»</Campo>
    </div>
  );
};

/**
 * Los datos de contacto y por dónde sale la respuesta.
 *
 * Están aquí y no en la ficha del expediente porque quien revisa decide en
 * segundos: mandarle a otra pantalla a comprobar el teléfono convierte una
 * decisión de diez segundos en dos minutos, y en la práctica significa que no
 * se comprueba.
 *
 * ⚠️ La LÍNEA DE SALIDA es el dato que no se puede deducir de ningún otro: la
 * elige el despachador y una respuesta tiene que salir del mismo número que
 * tuvo la conversación. Si sale por otro, el deudor recibe un mensaje de un
 * desconocido que le habla de su deuda.
 */
/**
 * QUÉ tarea se está revisando.
 *
 * 🔴 Faltaba, y sin ella no se puede juzgar el texto propuesto: «Enviar 1r
 * WhatsApp» y «Enviar 2o WhatsApp» son el MISMO tipo de tarea (`new_whatsapp`)
 * y piden mensajes opuestos — uno se presenta, el otro insiste en que no ha
 * habido respuesta. Un «te escribo de nuevo» es correcto en el segundo y un
 * disparate en el primero, y desde la pantalla no había forma de distinguirlo.
 */
const QueTarea = ({ tarea }) => {
  if (!tarea?.task_type) return null;
  return (
    <div className='px-4 py-2 border-b border-slate-200 bg-indigo-50/50 text-xs flex flex-wrap gap-x-2 items-baseline'>
      <span className='text-slate-400'>Revisas</span>
      <span className='font-semibold text-slate-800'>
        {TASK_TYPE_LABELS[tarea.task_type] || tarea.task_type}
      </span>
      {tarea.description && <span className='text-slate-700'>· «{tarea.description}»</span>}
      {tarea.origen === 'wa-script' && (
        <span className='text-slate-400'>· creada por el motor de WhatsApp</span>
      )}
      {/* 🔴 De qué tarea sale, y de quién es.
          Es la regla más básica del despachador —solo entran expedientes con un
          assignment del agente— y no había forma de comprobarla desde aquí. El
          14/09/2026 Edgar miró un caso y concluyó que había entrado sin tarea;
          sí la tenía. La pantalla no permitía verlo. */}
      {tarea.assigned_to && (
        <span
          className={
            tarea.assigned_to === 'AIAgent' ? 'text-slate-400' : 'text-amber-700 font-medium'
          }
        >
          ·{' '}
          {tarea.assigned_to === 'AIAgent'
            ? 'tarea del agente'
            : `tarea de una persona (${tarea.assigned_to})`}
          {tarea.status && tarea.status !== 'in_progress' && ` · ${tarea.status}`}
        </span>
      )}
      {tarea._id && (
        <span className='text-slate-300 font-mono' title='Id del assignment que originó este plan'>
          · {String(tarea._id).slice(-6)}
        </span>
      )}
    </div>
  );
};

const Contacto = ({ deudor, linea }) => {
  const tel = deudor?.debtorphone
    ? `+${deudor.country_prefix || '34'} ${deudor.debtorphone}`
    : null;

  // El `phone_id` del navegador es el nombre de una carpeta de Chrome
  // («Persona 1»), que no dice desde qué número sale. Por eso se pinta el
  // número y el nombre se deja como referencia de la línea.
  const salida = linea
    ? [linea.phone_number, linea.nombre || linea.phone_id].filter(Boolean).join(' · ')
    : null;

  return (
    <div className='px-4 py-2.5 border-b border-slate-200 bg-white flex flex-wrap gap-x-6 gap-y-1.5 text-xs'>
      <span className='text-slate-600'>
        <span className='text-slate-400'>Teléfono</span>{' '}
        {tel ? <span className='font-medium text-slate-800'>{tel}</span>
             : <span className='text-amber-700 font-medium'>no tiene</span>}
      </span>
      <span className='text-slate-600'>
        <span className='text-slate-400'>Email</span>{' '}
        {deudor?.debtormail ? <span className='font-medium text-slate-800'>{deudor.debtormail}</span>
                            : <span className='text-slate-400'>—</span>}
      </span>
      {/* 🔴 «Sale desde» es la línea de WhatsApp, y solo tiene sentido en las
          tareas de WhatsApp. El despachador solo sella esos tipos; un correo
          sale por SendGrid y no lleva línea ni la va a llevar nunca.
          Pintándolo igual para todos, cada `reply_email` de la cola salía con
          un «sin decidir todavía» en ámbar — como si algo fuera mal cuando lo
          que pasa es que ese campo no aplica. Edgar lo vio dos veces seguidas
          el 14/09/2026 y las dos lo leyó como un fallo, con razón: un aviso en
          ámbar significa que hay algo que mirar. */}
      {ES_DE_WHATSAPP.includes(linea?.tarea?.task_type) && (
        <span className='text-slate-600'>
          <span className='text-slate-400'>Sale desde</span>{' '}
          {salida ? (
            <span className='font-medium text-slate-800'>
              {salida}
              {linea.channel === 'navegador' && (
                <span className='text-slate-400 font-normal'> · por navegador</span>
              )}
              {linea.activa === false && (
                <span className='text-amber-700'> · línea inactiva</span>
              )}
            </span>
          ) : linea?.hold_reason ? (
            <span className='text-amber-700 font-medium'>retenida: {linea.hold_reason}</span>
          ) : (
            <span className='text-amber-700 font-medium'>sin decidir todavía</span>
          )}
        </span>
      )}
    </div>
  );
};

/**
 * Las otras tareas abiertas del expediente.
 *
 * Sirve para lo que no se ve mirando solo el plan: si propone un seguimiento
 * que YA existe, o si el mensaje nuevo deja obsoleta una tarea que habría que
 * cancelar. El agente no cancela tareas —no tiene herramienta para eso—, así
 * que hoy eso solo lo puede ver una persona, y solo si se lo enseñamos.
 */
const TareasAbiertas = ({ tareas }) => {
  if (!tareas || tareas.length === 0) return null;

  const cuando = (d) => {
    if (!d) return '—';
    const f = new Date(d);
    const dias = Math.round((f - new Date().setHours(0, 0, 0, 0)) / 86400000);
    const txt = f.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    if (dias < 0) return `${txt} · vencida`;
    if (dias === 0) return `${txt} · hoy`;
    return `${txt} · en ${dias} ${dias === 1 ? 'día' : 'días'}`;
  };

  return (
    <div className='px-4 py-3 border-t border-slate-200 bg-white'>
      <p className='text-xs font-semibold text-slate-500 uppercase mb-2'>
        Ya abiertas en este expediente ({tareas.length})
      </p>
      <div className='space-y-1'>
        {tareas.map((t) => {
          const vencida = t.date && new Date(t.date) < new Date().setHours(0, 0, 0, 0);
          return (
            <div key={t._id} className='flex gap-2 text-xs items-baseline flex-wrap'>
              <span className='font-medium text-slate-800'>
                {TASK_TYPE_LABELS[t.task_type] || t.task_type}
              </span>
              <span className={vencida ? 'text-amber-700 font-medium' : 'text-slate-500'}>
                {cuando(t.date)}
              </span>
              <span className='text-slate-400'>·</span>
              <span className='text-slate-500'>{t.assigned_name || 'sin dueño'}</span>
              {t.origen === 'wa-script' && (
                <span className='text-slate-400'>· del motor de WhatsApp</span>
              )}
              <span className='text-slate-600 truncate'>«{t.description}»</span>
            </div>
          );
        })}
      </div>
      <p className='text-[11px] text-slate-400 mt-2'>
        El agente no puede cancelarlas. Si alguna sobra con esta respuesta, ciérrala tú
        desde la ficha.
      </p>
    </div>
  );
};

/**
 * La tarea que se va a CANCELAR, con la misma forma que la que se va a crear.
 *
 * Misma ficha a propósito: quien revisa compara dos cosas del mismo tipo sin
 * cambiar de lenguaje. Lo único que cambia es el color —ámbar, porque esto
 * quita algo— y que se dice POR QUÉ sobra, que es lo que hay que juzgar.
 *
 * Los datos salen de las tareas abiertas del expediente, no de lo que el modelo
 * diga de ellas: si el id no casa con ninguna, se dice en vez de pintar un
 * hueco. Una ficha que se inventa lo que cancela es peor que no tenerla.
 */
const FichaDeCancelacion = ({ params: p, tareas }) => {
  const t = (tareas || []).find(x => String(x._id) === String(p.assignment_id));

  return (
    <div className='rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 space-y-1.5'>
      {t ? (
        <>
          <Campo etiqueta='Tarea'>{TASK_TYPE_LABELS[t.task_type] || t.task_type}</Campo>
          <Campo etiqueta='Vencía'>
            {t.date
              ? new Date(t.date).toLocaleDateString('es-ES', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })
              : '—'}
          </Campo>
          <Campo etiqueta='Era de'>{t.assigned_name || 'nadie'}</Campo>
          <Campo etiqueta='Descripción'>«{t.description}»</Campo>
        </>
      ) : (
        <Campo etiqueta='Tarea' aviso>
          No la encuentro entre las abiertas de este expediente (id {p.assignment_id})
        </Campo>
      )}
      <Campo etiqueta='Por qué sobra' aviso>
        {p.reason || '—'}
      </Campo>
    </div>
  );
};

/** Una acción propuesta, escrita como se le diría a una persona. */
/**
 * El mensaje que de verdad va a salir cuando la acción es una plantilla.
 *
 * Hasta el 20/10/2026 la tarjeta ponía «WhatsApp (plantilla)» y nada más: se
 * aprobaba sin ver el texto. Se toleraba porque la plantilla la elegía la
 * escalera y no había nada que decidir. **Desde que la elige el modelo, sí lo
 * hay**, y aprobar a ciegas es aprobar su elección sin verla.
 *
 * El texto sale ya rellenado con los datos de ESTE deudor, del mismo endpoint
 * que usa el chat. Si al expediente le falta algún dato, se ve el hueco aquí —
 * antes de que lo lea una persona.
 */
const PlantillaQueSaldra = ({ expedientId, elegida }) => {
  const [estado, setEstado] = React.useState({ cargando: true, fila: null, error: null });

  React.useEffect(() => {
    if (!expedientId) return undefined;
    let vivo = true;
    axios
      .get(`${baseAPI}/kapso/expedients/${expedientId}/templates`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then(({ data }) => {
        if (!vivo) return;
        const lista = data?.data?.templates || [];
        // La elegida por el modelo; sin elección, la que la escalera marca como
        // enviable. Si no cuadra ninguna, se dice, en vez de enseñar otra.
        const fila = elegida
          ? lista.find(t => t.key === elegida)
          : lista.find(t => t.selectable) || null;
        setEstado({ cargando: false, fila: fila || null, error: null });
      })
      .catch(err => vivo && setEstado({ cargando: false, fila: null, error: err }));
    return () => {
      vivo = false;
    };
  }, [expedientId, elegida]);

  if (estado.cargando) {
    return <p className='text-xs text-slate-400'>Cargando el texto de la plantilla…</p>;
  }

  if (!estado.fila) {
    return (
      <div className='rounded-lg bg-amber-50 border border-amber-200 px-3 py-2'>
        <p className='text-sm text-amber-900'>
          {elegida
            ? `No encuentro la plantilla «${elegida}» entre las de esta empresa.`
            : 'No he podido resolver qué plantilla saldría.'}
        </p>
        <p className='text-xs text-amber-800 mt-0.5'>
          Al aprobar, el servidor comprueba igualmente que esté aprobada por Meta; si no lo está, no
          sale.
        </p>
      </div>
    );
  }

  const { fila } = estado;
  const faltan = fila.missing_variables || [];

  return (
    <div className='rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2'>
      <p className='text-sm text-slate-800 whitespace-pre-wrap'>{fila.preview_text || '—'}</p>
      <p className='text-xs text-slate-500 mt-1'>
        Plantilla «{fila.description || fila.key}»
        {typeof fila.escalation_order === 'number'
          ? ` · peldaño ${fila.escalation_order} del contacto en frío`
          : ' · fuera de la escalera'}
      </p>
      {faltan.length > 0 && (
        <p className='text-xs text-red-700 mt-1'>
          Le faltan datos a este expediente: {faltan.join(', ')}. No saldría.
        </p>
      )}
    </div>
  );
};

const AccionLegible = ({ accion, estadoActual, deudor, bloquea, orden, tareasAbiertas, expedientId }) => {
  const p = accion.params || {};
  let titulo = accion.type;
  let cuerpo = null;

  if (accion.type === 'create_annotation') {
    titulo = 'Anotar en el expediente';
    cuerpo = <p className='text-sm text-slate-700'>{p.text || p.anotacion || '—'}</p>;
  } else if (accion.type === 'update_expedient_status') {
    titulo = 'Cambiar el estado del expediente';
    cuerpo = (
      <p className='text-sm'>
        <span className='px-2 py-0.5 rounded-lg bg-slate-100 text-slate-700'>
          {expedientStatusLabel(estadoActual) || '—'}
        </span>
        <span className='mx-2 text-slate-400'>→</span>
        <span className='px-2 py-0.5 rounded-lg bg-indigo-100 text-indigo-800 font-medium'>
          {expedientStatusLabel(p.new_status)}
        </span>
      </p>
    );
  } else if (accion.type === 'send_whatsapp_template') {
    titulo = `Enviar un WhatsApp a ${nombreDe(deudor).split(' ')[0]}`;
    cuerpo = <PlantillaQueSaldra expedientId={expedientId} elegida={p.template_key} />;
  } else if (CANALES[accion.type]) {
    titulo = `Enviar un ${CANALES[accion.type]} a ${nombreDe(deudor).split(' ')[0]}`;
    cuerpo = (
      <div className='rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2'>
        <p className='text-sm text-slate-800 whitespace-pre-wrap'>
          {p.message || p.body || p.text || '—'}
        </p>
        {p.subject && <p className='text-xs text-slate-500 mt-1'>Asunto: {p.subject}</p>}
        {/* Sin esto, aprobar un correo con un documento del cliente dentro se
            hacía a ciegas. Se dice el TIPO y no un nombre de fichero porque es
            la verdad: cuál documento se resuelve al enviar, no ahora. */}
        {p.attach_document_type && (
          <p className='text-xs text-slate-600 mt-1 flex items-center gap-1'>
            <PaperClipIcon className='w-3.5 h-3.5 shrink-0' />
            Adjunta un documento de tipo <strong>{p.attach_document_type}</strong> del
            expediente, de los marcados como enviables al deudor. Lo elige el sistema al
            enviar.
          </p>
        )}
      </div>
    );
  } else if (accion.type === 'create_follow_up_assignment') {
    titulo = 'Crear una tarea de seguimiento';
    cuerpo = <FichaDeLaTarea params={p} />;
  } else if (accion.type === 'cancel_assignment') {
    titulo = 'Cancelar una tarea que ya no hace falta';
    cuerpo = <FichaDeCancelacion params={p} tareas={tareasAbiertas} />;
  } else {
    cuerpo = (
      <pre className='text-xs bg-slate-50 rounded-lg p-2 overflow-x-auto text-slate-600'>
        {JSON.stringify(p, null, 2)}
      </pre>
    );
  }

  return (
    <div className='flex gap-3'>
      <div className='shrink-0 w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold flex items-center justify-center mt-0.5'>
        {orden}
      </div>
      <div className='min-w-0 flex-1'>
        <p className='text-sm font-medium text-slate-800 mb-1 flex items-center gap-2 flex-wrap'>
          <span>{titulo}</span>
          {bloquea && (
            <span className='text-[11px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-lg'>
              necesita tu visto bueno
            </span>
          )}
        </p>
        {cuerpo}
        {p.reason && accion.type === 'update_expedient_status' && (
          <p className='text-xs text-slate-500 mt-1'>Motivo: {p.reason}</p>
        )}
      </div>
    </div>
  );
};

const AIAgentReviewCase = ({ plan, contexto, rechazosPrevios = [], cargando, aMano = false }) => {
  // Antes de cualquier return condicional: los hooks no pueden llamarse
  // dentro de una rama.
  const [verTecnico, setVerTecnico] = React.useState(false);

  if (cargando) return <p className='text-sm text-slate-500 py-4'>Cargando el expediente…</p>;

  const real = contexto || {};
  const exp = real.expedient || {};
  const deudor = real.debtor;
  const cliente = real.client;
  const perfil = cliente?.company_profile || {};
  const tienePerfil = COMPANY_PROFILE_FIELDS.some(f => String(perfil[f] || '').trim());
  const conversacion = real.interactions || [];
  const acciones = plan.plan?.actions || [];
  const bloquean = plan.review?.blocking_actions || [];

  const novedades = real.novedades_del_deudor || 0;

  return (
    <div className='space-y-5'>
      {/* 🔴 El deudor ha escrito MIENTRAS esta propuesta esperaba.
          El plan no se entera: solo se retira cuando el agente escribe otro de
          la MISMA tarea. Así que al aprobarlo saldría un mensaje redactado sin
          haber leído esa respuesta — el deudor dice «ya he pagado» y le llega un
          «te recordamos que debes 784 €».
          Medido el 15/09/2026 sobre 10 días: pasó 7 veces y en 3 el mensaje
          llegó a salir. La ventana entre escribir y revisar tiene mediana de 14
          min, pero p90 de 124 y máximo de 971.
          NO bloquea la aprobación a propósito: a veces la respuesta no cambia
          nada, y quien revisa lo ve abajo, en su sitio de la conversación. */}
      {novedades > 0 && (
        <div className='rounded-lg border-2 border-amber-300 bg-amber-50 p-3'>
          <p className='text-sm font-semibold text-amber-900'>
            El deudor ha escrito {novedades === 1 ? 'un mensaje' : `${novedades} mensajes`} después
            de escribirse esta propuesta
          </p>
          <p className='text-xs text-amber-800 mt-0.5'>
            {novedades === 1 ? 'Va marcado' : 'Van marcados'} abajo en la conversación. La propuesta
            se redactó sin {novedades === 1 ? 'leerlo' : 'leerlos'}.
          </p>
        </div>
      )}

      {rechazosPrevios.length > 0 && (
        <div className='rounded-lg border border-amber-200 bg-amber-50 p-3'>
          <p className='text-xs font-semibold text-amber-900 mb-2'>
            Ya has corregido esto {rechazosPrevios.length}{' '}
            {rechazosPrevios.length === 1 ? 'vez' : 'veces'}
          </p>
          {rechazosPrevios.map(p => (
            <p key={p._id} className='text-xs text-amber-900'>
              <span className='font-medium'>
                Intento {p.attempt}
                {p.reviewer_name ? ` · ${p.reviewer_name}` : ''}:
              </span>{' '}
              «{p.review?.reason}»
            </p>
          ))}
        </div>
      )}

      {/* El perfil de empresa (IA) del cliente: su política de quitas, de planes
          de pago… Plegado, para no tener que ir a su perfil (23/10/2026). */}
      {tienePerfil && (
        <details className='rounded-lg border border-slate-200 bg-white'>
          <summary className='cursor-pointer px-4 py-2 text-sm font-medium text-slate-700'>
            Perfil de empresa (IA) de {exp.nombre_comercial || nombreCliente(cliente) || 'este cliente'}
          </summary>
          <dl className='px-4 pb-3 space-y-2 text-sm'>
            {COMPANY_PROFILE_FIELDS.filter(f => String(perfil[f] || '').trim()).map(f => (
              <div key={f}>
                <dt className='text-xs font-semibold text-slate-500'>{COMPANY_PROFILE_FIELD_LABELS[f] || f}</dt>
                <dd className='text-slate-800 whitespace-pre-wrap'>{perfil[f]}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}

      {/* LA REALIDAD */}
      <div className='rounded-lg border border-slate-200 overflow-hidden'>
        <div className='bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-start justify-between gap-3 flex-wrap'>
          <div>
            <p className='text-sm font-semibold text-slate-900'>{nombreDe(deudor)}</p>
            <p className='text-xs text-slate-500'>
              {exp.nombre_comercial || nombreCliente(cliente) || 'Cliente desconocido'}
              {exp.tipo_deuda ? ` · ${exp.tipo_deuda}` : ''}
            </p>
          </div>
          <div className='text-right'>
            <p className='text-sm font-semibold text-slate-900'>
              {euros(exp.importe_deuda, exp.divisa)}
            </p>
            <p className='text-xs text-slate-500'>
              {expedientStatusLabel(exp.estado)}
              {exp._id && (
                <>
                  {' · '}
                  {/* La ficha del expediente es /view/:id — /expedients es el
                      listado completo, que no sirve de nada aquí. Se abre en
                      otra pestaña para no perder la cola de revisión. */}
                  <a
                    href={`/view/${exp._id}`}
                    target='_blank'
                    rel='noreferrer'
                    className='text-indigo-600 hover:underline'
                  >
                    ver ficha
                  </a>
                </>
              )}
            </p>
          </div>
        </div>

        <QueTarea tarea={real.linea_salida?.tarea} />
        <Contacto deudor={deudor} linea={real.linea_salida} />

        <div className='p-4 bg-slate-50/50'>
          <p className='text-xs font-semibold text-slate-500 uppercase mb-3'>
            La conversación
          </p>
          {conversacion.length === 0 ? (
            <p className='text-sm text-slate-500'>Sin comunicaciones registradas.</p>
          ) : (
            conversacion.map(i => <Mensaje key={i._id} i={i} />)
          )}
        </div>

        <TareasAbiertas tareas={real.tareas_abiertas} />
      </div>

      {/* LA PROPUESTA. Tras «Lo hago yo» queda en gris: ya no se puede aprobar,
          pero sigue a la vista como referencia de lo que propuso. */}
      <div
        aria-disabled={aMano}
        className={`rounded-lg border border-slate-200 p-4 ${aMano ? 'opacity-50 pointer-events-none select-none' : ''}`}
      >
        <p className='text-xs font-semibold text-slate-500 uppercase mb-2'>
          {aMano ? 'Lo que proponía el agente (no aprobado)' : 'Qué propone el agente'}
          {plan.situation?.peldano > 0 && (
            <span className='ml-2 normal-case font-medium text-slate-700'>
              · Peldaño {Math.min(plan.situation.peldano, PELDANOS)} de {PELDANOS}
            </span>
          )}
        </p>
        {plan.situation?.avisos?.length > 0 && (
          <div className='rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 mb-3'>
            <p className='text-xs font-semibold text-amber-800 mb-1'>
              Se le devolvió una vez y sigue incompleta:
            </p>
            <ul className='list-disc pl-4 text-sm text-amber-900 space-y-0.5'>
              {plan.situation.avisos.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </div>
        )}
        {plan.plan?.rationale && (
          <p className='text-sm text-slate-700 italic mb-4'>«{plan.plan.rationale}»</p>
        )}

        <div className='space-y-4'>
          {acciones.map((a, i) => (
            <AccionLegible
              key={i}
              orden={i + 1}
              accion={a}
              estadoActual={exp.estado}
              deudor={deudor}
              bloquea={bloquean.includes(a.type)}
              tareasAbiertas={real.tareas_abiertas}
              expedientId={exp._id}
            />
          ))}
        </div>

        <button
          type='button'
          onClick={() => setVerTecnico(v => !v)}
          className='mt-4 text-xs text-slate-400 hover:text-slate-600'
        >
          {verTecnico ? 'Ocultar detalle técnico' : 'Ver detalle técnico'}
        </button>
        {verTecnico && (
          <pre className='mt-2 text-xs bg-slate-50 rounded-lg p-3 overflow-x-auto text-slate-600'>
            {JSON.stringify({ situation: plan.situation, actions: acciones }, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
};

export default AIAgentReviewCase;
